import { useEffect, useMemo, useRef, useState } from "react"
import isEqual from "lodash/isEqual"
import debounce from "lodash/debounce"

/**
 * Debounces any property that you pass in it to the defined delay.
 * @param prop Desired property
 * @param delay Delay for debounce effect. Defaults to 300ms.
 * @param initialValue initialValue for property
 * @returns Debounced on change property.
 */

export const useDebouncedProperty = <T>(
  property: T,
  delay = 300,
  initialValue = null
): [T] => {
  const previousKeysRef = useRef(property)

  const [debouncedProperty, setDebouncedProperty] = useState<T>(initialValue)

  const updateDebouncedProp = useMemo(
    () =>
      debounce((input: T) => {
        setDebouncedProperty(input)

        previousKeysRef.current = input
      }, delay),
    [delay]
  )

  useEffect(() => {
    if (!isEqual(property, previousKeysRef.current)) {
      updateDebouncedProp(property)
    }

    return () => updateDebouncedProp.cancel()
  }, [property, updateDebouncedProp])

  return [debouncedProperty]
}
